// Implements: F-UIX-019
/**
 * The two notices the status payload can raise above the platform view (UIX.8):
 *
 * - "Límites desactivados", red, when `caps_disabled` (`USAGE_ENFORCEMENT_ENABLED=false`,
 *   USG.15): no generation is refused for usage while it shows.
 * - The global spend alert, when `global_alert.crossed` (USG.17): it names the month and
 *   never an amount.
 *
 * - "Saldo del proveedor bajo", amber, when `provider_credits.state` is "low": it names the
 *   remaining amount (the admin module is where money may be written) and points at topping up.
 *
 * Nothing renders while none holds. Tone is a second signal, never the only one: each
 * notice carries its own title and sentence.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Icon } from "../../../shared/ui/atoms/Icon";
import type { SystemStatusView } from "../../../types/status";
import { costText } from "../model/money";
import { monthText } from "../model/status";
import styles from "./StatusPanel.module.css";

export interface StatusNoticesProps {
  readonly view: SystemStatusView;
}

export function StatusNotices({ view }: StatusNoticesProps): ReactElement | null {
  const { t } = useTranslation();
  const capsTitle = useId();
  const alertTitle = useId();
  const creditsTitle = useId();
  const capsDisabled = view.caps_disabled;
  const crossed = view.global_alert.crossed;
  const credits = view.provider_credits;
  const creditsLow = credits.state === "low";
  if (!capsDisabled && !crossed && !creditsLow) return null;

  return (
    <div className={styles.notices}>
      {capsDisabled ? (
        <div className={[styles.notice, styles.danger].join(" ")} role="alert" aria-labelledby={capsTitle} data-tone="danger">
          <Icon name="alert-circle" />
          <div>
            <p id={capsTitle} className={styles.noticeTitle}>
              {t("admin.status.capsNotice.title")}
            </p>
            <p className={styles.noticeBody}>{t("admin.status.capsNotice.body")}</p>
          </div>
        </div>
      ) : null}
      {crossed ? (
        <div
          className={[styles.notice, styles.warning].join(" ")}
          role="status"
          aria-labelledby={alertTitle}
          data-tone="warning"
        >
          <Icon name="alert-triangle" />
          <div>
            <p id={alertTitle} className={styles.noticeTitle}>
              {t("admin.status.alertNotice.title")}
            </p>
            <p className={styles.noticeBody}>
              {t("admin.status.alertNotice.body", { month: monthText(view.global_alert.month) })}
            </p>
          </div>
        </div>
      ) : null}
      {creditsLow ? (
        <div
          className={[styles.notice, styles.warning].join(" ")}
          role="status"
          aria-labelledby={creditsTitle}
          data-tone="warning"
        >
          <Icon name="alert-triangle" />
          <div>
            <p id={creditsTitle} className={styles.noticeTitle}>
              {t("admin.status.creditsNotice.title")}
            </p>
            <p className={styles.noticeBody}>
              {credits.remaining_micros === null
                ? t("admin.status.creditsNotice.bodyUnknown")
                : t("admin.status.creditsNotice.body", { amount: costText(credits.remaining_micros, credits.currency) })}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
